/** @jsx jsx */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { jsx } from '@emotion/core';
import { Box, Grid, Typography } from '@material-ui/core';
import * as braintree from 'braintree-web';

import styles from '../styles';

import useTranslate, {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';

import { TextFieldPopover } from 'components/TextField';
import { useSnackbar } from 'notistack';

import useBraintreeTokens, {
  BraintreeTokensError,
} from 'hooks/requests/braintreeTokens';
import { HostedFields } from 'braintree-web';
import { useTheme } from 'emotion-theming';
import { Theme } from 'features/Theme';
import { decimalMoneyMask, conformToMask } from 'utils/masks';
import { NonClientError } from 'hooks/requests/fetchUtils';
import catchOnly from 'utils/catchOnly';
import FocusableHelpOutlineIcon from 'components/TextField/FocusableHelpOutlineIcon';

export interface FormValues {
  consent: boolean;
}

type PropTypes = {
  biweeklyPayment: number;
  setHostedFieldsValidity: React.Dispatch<React.SetStateAction<boolean>>;
  setHostedFieldsInstance: React.Dispatch<
    React.SetStateAction<HostedFields | undefined>
  >;
};

const BrainTree: React.FC<PropTypes> = props => {
  const {
    biweeklyPayment,
    setHostedFieldsValidity,
    setHostedFieldsInstance,
  } = props;

  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const tMdMultiline = useMarkdownMultilineTranslate();
  const cvvRef = useRef<HTMLElement | null>(null);
  const [braintreeTokens] = useBraintreeTokens();
  const theme = useTheme<Theme>();
  const { enqueueSnackbar } = useSnackbar();

  const [token, setToken] = useState<string>('');

  const getBraintreeToken = useCallback(async () => {
    try {
      const braintreeTokensResult = await braintreeTokens();
      const { token } = braintreeTokensResult;

      setToken(token);
    } catch (e) {
      if (catchOnly(e, NonClientError, BraintreeTokensError)) {
        enqueueSnackbar(t('unified.placeOrder.hostedFields.braintreeError'), {
          variant: 'error',
          persist: true,
        });
      }
    }
  }, [braintreeTokens, enqueueSnackbar, t]);

  const setupBraintree = useCallback(async () => {
    if (!token) {
      return;
    }
    try {
      const clientInstance = await braintree.client.create({
        authorization: token,
      });

      const options = {
        client: clientInstance,
        styles: {
          input: {
            // Must be hard coded in pixel because the iframe doesn't inherit
            // the base font size.
            'font-size': '16px',
          },
          'input.valid': {
            color: theme.mui.palette.success.main,
          },
          'input.invalid': {
            color: theme.mui.palette.error.main,
          },
        },
        fields: {
          number: {
            container: '#card-number',
            placeholder: '4111 1111 1111 1111',
          },
          cvv: {
            container: '#cvv',
            placeholder: '123',
          },
          expirationDate: {
            container: '#expiration-date',
            placeholder: 'MM/YYYY',
          },
          // Braintree does not seem to allow for enough formatting to prevent
          // letters from being entered in the postalcode field
          postalCode: {
            container: '#postal-code',
            placeholder: '12345',
            maxlength: 5,
            minlength: 5,
          },
        },
      };

      const hostedFieldsInstance = await braintree.hostedFields.create(options);

      hostedFieldsInstance.on('validityChange', event => {
        const fields = event.fields;

        const isValid =
          fields.number.isValid &&
          fields.expirationDate.isValid &&
          fields.postalCode.isValid &&
          fields.cvv.isValid;

        setHostedFieldsValidity(isValid);
      });

      setHostedFieldsInstance(hostedFieldsInstance);
    } catch (e) {
      // Can't import the actual BraintreeError class. The TS definitions only
      // export this as an interface.
      // We'll just depends on e.name instead.
      if (e.name === 'BraintreeError') {
        enqueueSnackbar(t('unified.placeOrder.hostedFields.braintreeError'), {
          variant: 'error',
          persist: true,
        });
      } else {
        throw e;
      }
    }
  }, [
    enqueueSnackbar,
    setHostedFieldsInstance,
    setHostedFieldsValidity,
    t,
    theme.mui.palette.error.main,
    theme.mui.palette.success.main,
    token,
  ]);

  useEffect(() => {
    getBraintreeToken();
  }, [getBraintreeToken]);

  useEffect(() => {
    setupBraintree();
  }, [token, setHostedFieldsInstance, setHostedFieldsValidity, setupBraintree]);

  return (
    <Box mb={3} css={styles.shadedRoundedContainer}>
      <p css={styles.hostedFieldsTitle}>
        {tMd('unified.placeOrder.hostedFields.title')}
      </p>
      <p css={styles.hostedFieldsSubtitle}>
        {tMd('unified.placeOrder.hostedFields.subtitle')}
      </p>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div css={styles.hostedFieldSection}>
            <label htmlFor="card-number" css={styles.hostedFieldLabel}>
              {tMd('unified.placeOrder.hostedFields.cardNumber')}
            </label>
            <div id="card-number" css={styles.hostedField}></div>
          </div>

          <div css={styles.cardImages}>
            <img
              src={`data:image/png;base64,${t(
                'unified.placeOrder.hostedFields.visaIcon',
              )}`}
              alt="visa"
              css={styles.cardImage}
            />
            <img
              src={`data:image/png;base64,${t(
                'unified.placeOrder.hostedFields.mastercardIcon',
              )}`}
              alt="mastercard"
              css={styles.cardImage}
            />
            <img
              src={`data:image/png;base64,${t(
                'unified.placeOrder.hostedFields.discoverIcon',
              )}`}
              alt="discover"
              css={styles.cardImage}
            />
            <img
              src={`data:image/png;base64,${t(
                'unified.placeOrder.hostedFields.amexIcon',
              )}`}
              alt="american express"
              css={styles.cardImage}
            />
          </div>
        </Grid>

        <Grid item xs={6}>
          <div css={styles.hostedFieldSection}>
            <label htmlFor="expiration-date" css={styles.hostedFieldLabel}>
              {tMd('unified.placeOrder.hostedFields.expirationDate')}
            </label>
            <div id="expiration-date" css={styles.hostedField}></div>
          </div>
        </Grid>

        <Grid item xs={6}>
          <div css={styles.hostedFieldSection}>
            <label htmlFor="cvv" css={styles.hostedFieldLabel}>
              {tMd('unified.placeOrder.hostedFields.cvv')}
            </label>
            <div id="cvv" css={styles.hostedField}></div>

            <span css={styles.enquiryIcon}>
              <FocusableHelpOutlineIcon tabIndex={0} innerRef={cvvRef} />
            </span>
          </div>

          <TextFieldPopover id="cvvPopover" textFieldRef={cvvRef}>
            <Typography variant="body2" component="div">
              {tMdMultiline('unified.placeOrder.hostedFields.cvvPopover')}
            </Typography>
          </TextFieldPopover>
        </Grid>

        <Grid item xs={12}>
          <div css={styles.hostedFieldSection}>
            <label htmlFor="postal-code" css={styles.hostedFieldLabel}>
              {tMd('unified.placeOrder.hostedFields.postalCode')}
            </label>
            <div id="postal-code" css={styles.hostedField}></div>
          </div>
        </Grid>

        <Grid item xs={12}>
          <p css={styles.hostedFieldsFooter}>
            {tMd('unified.placeOrder.hostedFields.footer', {
              biweeklyPayment: conformToMask(
                biweeklyPayment.toFixed(),
                decimalMoneyMask,
              ),
            })}
          </p>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BrainTree;
